import {
  API_BASE_URL,
  ApiError,
  fetchWithAuth,
  SessionExpiredError,
  type SessionExpiryReason,
  type FetchWithAuthOptions,
} from '@/lib/apiClient';
import type {
  PatientSessionUser,
  PortalPatient,
  PortalProfile,
  PortalProfileEdit,
  PortalReferralDetail,
  PortalReferralSummary,
} from '@/types/patientPortal';

export const PATIENT_SIGN_IN_PATH = '/profile/sign-in';
export const PATIENT_HOME_PATH = '/profile';
export const PATIENT_DETAILS_PATH = '/profile/details';

/** The prefix every patient query shares: dropped when a session ends or starts. */
export const PATIENT_PORTAL_QUERY_PREFIX = ['patientPortal'] as const;

export const patientPortalQueryKeys = {
  appointmentRequests: ['patientPortal', 'appointmentRequests'] as const,
  appointmentRequest: (id: string) =>
    ['patientPortal', 'appointmentRequest', id] as const,
  profile: ['patientPortal', 'profile'] as const,
};

/**
 * Every signed-in patient call goes through here: the shared client's expiry
 * handler clears staff storage and lands on /login, which a patient has no
 * use for.
 */
function patientSignInRedirect(reason: SessionExpiryReason) {
  // A refresh outage is the page's retry message, not a new code.
  if (reason === 'unavailable') return;
  if (window.location.pathname === PATIENT_SIGN_IN_PATH) return;
  window.location.href = PATIENT_SIGN_IN_PATH;
}

function patientFetch<T>(
  path: string,
  options: FetchWithAuthOptions = {}
): Promise<T> {
  return fetchWithAuth<T>(`${API_BASE_URL}${path}`, {
    onSessionExpired: patientSignInRedirect,
    ...options,
  });
}

export interface PatientSession {
  user: PatientSessionUser;
  patients: PortalPatient[];
}

/**
 * Null when the API rejected the refresh: there is no session. A refresh
 * endpoint that is down throws, so the gate offers a retry instead of a code.
 */
export async function getPatientSession(): Promise<PatientSession | null> {
  try {
    const response = await patientFetch<{
      user: PatientSessionUser;
      patients?: PortalPatient[];
    }>('/auth/me', { onSessionExpired: () => {}, timeoutMs: 5_000 });
    return { user: response.user, patients: response.patients ?? [] };
  } catch (error) {
    if (error instanceof SessionExpiredError) return null;
    throw error;
  }
}

/** Always 202: the API never says whether the email has an account. */
export function requestPatientSignInCode(email: string): Promise<void> {
  return patientFetch('/auth/patient-code', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

const GENERIC_FAILURE = 'Something went wrong. Try again.';
// The route throttle's 429 body is Nest's own wording, not patient copy.
const TOO_MANY_ATTEMPTS = 'Too many attempts. Wait a minute and try again.';

export type SignInFailure = 'code_rejected' | 'locked_out' | 'other';

/** Why a code request or a sign-in failed: 401 is the code, 429 the throttle. */
export function classifySignInFailure(error: unknown): SignInFailure {
  if (!(error instanceof ApiError)) return 'other';
  if (error.status === 401) return 'code_rejected';
  if (error.status === 429) return 'locked_out';
  return 'other';
}

/** What to show for it. A wrong code keeps the API's words ("Invalid or expired code"). */
export function signInFailureMessage(error: unknown): string {
  switch (classifySignInFailure(error)) {
    case 'code_rejected':
      return (error as ApiError).message;
    case 'locked_out':
      return TOO_MANY_ATTEMPTS;
    default:
      return GENERIC_FAILURE;
  }
}

/** A wrong or expired code is an ApiError with status 401; a lockout, 429. */
export function patientSignIn(
  email: string,
  code: string
): Promise<{ user: PatientSessionUser }> {
  return patientFetch('/auth/patient-sign-in', {
    method: 'POST',
    body: JSON.stringify({ email, code }),
    skipRedirectOn401: true,
  });
}

export function patientSignOut(): Promise<{ message: string }> {
  return patientFetch('/auth/logout', {
    method: 'POST',
    skipRedirectOn401: true,
  });
}

export async function listAppointmentRequests(): Promise<
  PortalReferralSummary[]
> {
  const { appointmentRequests } = await patientFetch<{
    appointmentRequests: PortalReferralSummary[];
  }>('/appointment-requests');
  return appointmentRequests;
}

export async function getAppointmentRequest(
  id: string
): Promise<PortalReferralDetail> {
  const { appointmentRequest } = await patientFetch<{
    appointmentRequest: PortalReferralDetail;
  }>(`/appointment-requests/${id}`);
  return appointmentRequest;
}

export async function getProfile(): Promise<PortalProfile> {
  const { profile } = await patientFetch<{ profile: PortalProfile }>(
    '/profile'
  );
  return profile;
}

/**
 * 409 with the API's copy when every request is closed, or when patient
 * information is sent while the open requests are for several patients.
 */
export async function updateProfile(
  edit: PortalProfileEdit
): Promise<PortalProfile> {
  const { profile } = await patientFetch<{ profile: PortalProfile }>(
    '/profile',
    { method: 'PATCH', body: JSON.stringify(edit) }
  );
  return profile;
}

/** 409 when the request is already closed. */
export async function requestCancel(
  id: string
): Promise<{ requestedAt: string }> {
  const { cancelRequest } = await patientFetch<{
    cancelRequest: { requestedAt: string };
  }>(`/appointment-requests/${id}/cancel-request`, { method: 'POST' });
  return cancelRequest;
}
